import { useEffect, useRef, useState } from "react";
import { DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adjustmentOf, draftFor, leavesPriceUnset, payeeOf, serviceChoices, type Adjustable, type AdjustmentDraft, type ListedMoney } from "@/lib/appointment-adjustment";
import { fieldActionMessage, isConflictRefusal } from "@/lib/appointment-field-actions";
import { typedMoneyProblem } from "@/lib/typed-money";
import type { AppointmentJoin } from "@/repositories/appointments.repository";
import { adjustAppointment } from "@/services/appointment-change.service";
import { fetchListedMoney, fetchVendorChildServices, fetchVendorParentCategories } from "../appointments.api";

interface AdjustAppointmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentJoin;
  onSuccess?: (updatedAppointment: AppointmentJoin) => void;
  onRefetch?: () => void;
}

type MoneySource = "current" | "listed" | "none";

const PRICE_HINTS: Record<MoneySource, string> = {
  current: "The appointment's current price. You can change it.",
  listed: "The selected service's listed price at this property. You can change it.",
  none: "No listed price was found for this service. Type the price.",
};

const TYPE_A_PRICE = "Type the price to save.";

const CONFIRM_THE_PRICE = "This price has not been set for the job yet. Save to confirm it, or change it.";

const NO_PRICE_YET = "This appointment has no price yet. Type one to set it.";

function priceHint(start: Adjustable, draft: AdjustmentDraft, source: MoneySource): string {
  if (leavesPriceUnset(start, draft)) return NO_PRICE_YET;
  if (draft.price.trim() === "" && source !== "none") return TYPE_A_PRICE;
  return source === "current" && start.priceMissing ? CONFIRM_THE_PRICE : PRICE_HINTS[source];
}

const dollars = (amount: number | null) => (amount === null ? "no amount" : `$${amount.toFixed(2)}`);

function MoneyField({ id, label, value, onChange, disabled, hint }: { id: string; label: string; value: string; onChange: (text: string) => void; disabled: boolean; hint: string | null }) {
  const problem = typedMoneyProblem(value);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input id={id} type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} className="pl-10" placeholder="0.00" disabled={disabled} />
        {disabled && <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
      {problem && <p className="text-sm text-destructive">{problem}</p>}
    </div>
  );
}

function adjustableOf(appointment: AppointmentJoin) {
  const payee = payeeOf(appointment);
  const start: Adjustable = {
    service: appointment.service.id,
    price: appointment.final_price ?? null,
    pay: payee?.amount ?? null,
    priceMissing: appointment.billing_exception?.missing.includes("price") ?? false,
  };
  return { payee, payShown: payee !== null && !payee.paid, start };
}

export default function AdjustAppointmentDialog({ isOpen, onClose, appointment, onSuccess, onRefetch }: AdjustAppointmentDialogProps) {
  const { payee, payShown, start } = adjustableOf(appointment);
  const [parentService, setParentService] = useState("");
  const [parentChoices, setParentChoices] = useState<string[]>([]);
  const [childList, setChildList] = useState<{ id: string; name: string }[]>([]);
  const [draft, setDraft] = useState<AdjustmentDraft>(() => draftFor(start, start.service, null, payShown));
  const [priceSource, setPriceSource] = useState<MoneySource>("current");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingListed, setLoadingListed] = useState(false);
  const latestLookup = useRef(0);

  const adjustment = adjustmentOf(start, draft);

  useEffect(() => {
    setRefusal(null);
  }, [isOpen]);

  useEffect(() => {
    latestLookup.current += 1;
    setLoadingListed(false);
    if (!isOpen) {
      setParentService("");
      setChildList([]);
      return;
    }
    const opened = adjustableOf(appointment);
    setDraft(draftFor(opened.start, opened.start.service, null, opened.payShown));
    setPriceSource("current");
    setParentService(appointment.service.parent_name ?? "");
    setLoadingServices(true);
    fetchVendorParentCategories()
      .then(setParentChoices)
      .catch(() => {
        toast.error("Failed to load service categories");
        setParentChoices([]);
      })
      .finally(() => setLoadingServices(false));
  }, [isOpen, appointment]);

  const currentService = appointment.service;

  useEffect(() => {
    if (!isOpen || !parentService) return;
    setLoadingServices(true);
    fetchVendorChildServices(parentService)
      .then((live) => {
        const services = serviceChoices(parentService, live, currentService);
        setChildList(services);
        setDraft((current) => (services.some((service) => service.id === current.service) ? current : { ...current, service: "" }));
      })
      .catch(() => {
        toast.error("Failed to load services");
        setChildList([]);
      })
      .finally(() => setLoadingServices(false));
  }, [parentService, isOpen, currentService]);

  const pickService = async (service: string) => {
    if (service === "" || service === draft.service) return;
    const lookup = ++latestLookup.current;
    setRefusal(null);
    if (service === start.service) {
      setLoadingListed(false);
      setDraft(draftFor(start, service, null, payShown));
      setPriceSource("current");
      return;
    }
    setDraft((current) => ({ ...current, service }));
    setLoadingListed(true);
    let listed: ListedMoney = { price: null, pay: null };
    try {
      listed = await fetchListedMoney(appointment.vendor.id, appointment.property.id, service);
    } catch {
      toast.error("Failed to load the service's listed price");
    }
    if (lookup !== latestLookup.current) return;
    setDraft(draftFor(start, service, listed, payShown));
    setPriceSource(listed.price === null ? "none" : "listed");
    setLoadingListed(false);
  };

  const save = async () => {
    if (adjustment === null) return;
    setSaving(true);
    setRefusal(null);
    try {
      const updated = await adjustAppointment(appointment.id, adjustment);
      toast.success("Service, price and payout updated");
      onSuccess?.(updated);
      onClose();
    } catch (error) {
      setRefusal(fieldActionMessage(error, "Failed to adjust the service, price and payout"));
      if (isConflictRefusal(error)) onRefetch?.();
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Adjust service, price and payout</DialogTitle>
          <DialogDescription>Correct the service, what the customer is billed and what is paid out. A draft invoice follows the new service and price.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Service Category</Label>
            <Select value={parentService} onValueChange={setParentService} disabled={loadingServices}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select service category" />
              </SelectTrigger>
              <SelectContent>
                {parentChoices.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Sub Service</Label>
            <Select value={draft.service} onValueChange={(service) => void pickService(service)} disabled={!parentService || loadingServices}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select sub service" />
              </SelectTrigger>
              <SelectContent>
                {childList.length > 0 ? (
                  childList.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">{parentService ? "No services available" : "Select a service category first"}</div>
                )}
              </SelectContent>
            </Select>
          </div>

          <MoneyField
            id="adjust-price"
            label="Price"
            value={draft.price}
            onChange={(price) => setDraft((current) => ({ ...current, price }))}
            disabled={loadingListed}
            hint={priceHint(start, draft, priceSource)}
          />

          {payShown && (
            <MoneyField
              id="adjust-pay"
              label={`Pay to ${payee.name}`}
              value={draft.pay ?? ""}
              onChange={(pay) => setDraft((current) => ({ ...current, pay }))}
              disabled={loadingListed}
              hint={null}
            />
          )}
          {payee?.paid && (
            <p className="text-sm text-muted-foreground">
              {payee.name}'s pay of {dollars(payee.amount)} has been paid, so it can no longer change here.
            </p>
          )}

          {refusal && (
            <p role="alert" className="text-sm text-destructive">
              {refusal}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={adjustment === null || loadingListed || saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
